import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { MdTag } from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import SupplierSelector from '../../components/SupplierSelector';
import {
  DELIVERY_TO_LABELS,
  PRODUCTION_PAID_LABELS,
  productionProductOptions,
  supplierCategoryForProduct,
  type ProductionDateEntry,
} from '../../components/ProjectWorkflow';
import {
  createProjectSupplierReference,
  deleteProjectSupplierReference,
  listProjectSupplierReferences,
  updateProjectSupplierReference,
} from './supplierReferences.service';
import type { ProjectSupplierReference } from './supplierReferences.types';
import { groupSupplierOrders } from './supplierOrders';

type SupplierOption = {
  id: string;
  supplier_name: string;
};

interface SupplierOrdersCardProps {
  projectId: string;
  suppliers: SupplierOption[];
  preferredSupplierIds?: string[];
  chosenSupplierIds?: string[];
  productionDates: ProductionDateEntry[];
  onProductionDatesChange: (dates: ProductionDateEntry[]) => void;
  productTypes?: string[] | null;
  readOnly?: boolean;
}

type ReferenceForm = {
  id: string | null;
  groupKey: string;
  supplier_id: string;
  reference: string;
  notes: string;
};

const UNASSIGNED_KEY = '__unassigned';

/** Small labels above each field on an order line. */
const ORDER_LABEL_SX = { fontSize: 'xs', color: 'neutral.600', mb: 0.25 } as const;

/** Text box that saves when you leave it (or press Enter), not on every keystroke. */
const CommitInput: React.FC<{
  value: string;
  onCommit: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  sx?: React.ComponentProps<typeof Input>['sx'];
}> = ({ value, onCommit, ...rest }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => { if (draft !== value) onCommit(draft); };
  return (
    <Input
      size="sm"
      {...rest}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === 'Enter') commit(); }}
    />
  );
};

const SupplierOrdersCard: React.FC<SupplierOrdersCardProps> = ({
  projectId,
  suppliers,
  preferredSupplierIds = [],
  chosenSupplierIds = [],
  productionDates,
  onProductionDatesChange,
  productTypes,
  readOnly = false,
}) => {
  const [references, setReferences] = useState<ProjectSupplierReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ReferenceForm | null>(null);
  const [extraSupplierIds, setExtraSupplierIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReferences(await listProjectSupplierReferences(projectId));
    } catch (err) {
      setReferences([]);
      setError(err instanceof Error ? err.message : 'Failed to load supplier references');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const listedSupplierIds = useMemo(() => {
    const base = chosenSupplierIds.length ? chosenSupplierIds : preferredSupplierIds;
    return [...base, ...extraSupplierIds];
  }, [chosenSupplierIds, preferredSupplierIds, extraSupplierIds]);

  const groups = useMemo(() => groupSupplierOrders({
    suppliers,
    listedSupplierIds,
    references,
    productionDates,
  }), [suppliers, listedSupplierIds, references, productionDates]);

  const shownSupplierIds = groups.map((group) => group.supplierId).filter(Boolean) as string[];
  const addableSuppliers = suppliers.filter((row) => !shownSupplierIds.includes(row.id));
  const supplierName = (id: string) => suppliers.find((row) => row.id === id)?.supplier_name || null;

  const startEditReference = (groupKey: string, row: ProjectSupplierReference) => {
    setError(null);
    setForm({
      id: row.id,
      groupKey,
      supplier_id: row.supplier_id || '',
      reference: row.reference,
      notes: row.notes || '',
    });
  };

  const saveReference = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        supplier_id: form.supplier_id || null,
        supplier_name: supplierName(form.supplier_id),
        reference: form.reference,
        notes: form.notes,
      };
      if (form.id) {
        const updated = await updateProjectSupplierReference(form.id, payload);
        setReferences((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      } else {
        const created = await createProjectSupplierReference(projectId, payload);
        setReferences((current) => [created, ...current]);
      }
      setForm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save supplier reference');
    } finally {
      setSaving(false);
    }
  };

  const removeReference = async (row: ProjectSupplierReference) => {
    if (!window.confirm(`Remove supplier reference "${row.reference}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteProjectSupplierReference(row.id);
      setReferences((current) => current.filter((item) => item.id !== row.id));
      if (form?.id === row.id) setForm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete supplier reference');
    } finally {
      setSaving(false);
    }
  };

  const addProductionDate = (supplierId: string) => {
    const selected = (productTypes || []).map((type) => String(type).trim()).filter(Boolean);
    const options = productionProductOptions(productTypes || undefined);
    onProductionDatesChange([
      ...productionDates,
      {
        id: Date.now().toString(),
        productType: selected[0] || options[0] || 'Glass',
        supplierId,
        date: '',
        paid: null,
      },
    ]);
  };

  const updateProductionDate = (id: string, field: keyof ProductionDateEntry, value: string | null) => {
    onProductionDatesChange(productionDates.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
  };

  const removeProductionDate = (id: string) => {
    onProductionDatesChange(productionDates.filter((entry) => entry.id !== id));
  };

  const renderReferenceForm = () => form && (
    <Stack gap={1.25} sx={{ p: 1.25, borderRadius: 'sm', bgcolor: 'background.level1' }}>
      <FormControl size="sm">
        <FormLabel>Supplier</FormLabel>
        <Select
          size="sm"
          placeholder="Select supplier"
          value={form.supplier_id || null}
          onChange={(_, value) => setForm((current) => (current ? { ...current, supplier_id: String(value || '') } : current))}
        >
          {suppliers.map((row) => (
            <Option key={row.id} value={row.id}>{row.supplier_name}</Option>
          ))}
        </Select>
      </FormControl>
      <FormControl size="sm">
        <FormLabel>Their reference</FormLabel>
        <Input
          size="sm"
          value={form.reference}
          onChange={(event) => setForm((current) => (current ? { ...current, reference: event.target.value } : current))}
          placeholder="e.g. SO-48291"
        />
      </FormControl>
      <FormControl size="sm">
        <FormLabel>Notes (optional)</FormLabel>
        <Textarea
          size="sm"
          minRows={2}
          value={form.notes}
          onChange={(event) => setForm((current) => (current ? { ...current, notes: event.target.value } : current))}
          placeholder="e.g. Production order, glass order"
        />
      </FormControl>
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button size="sm" variant="plain" disabled={saving} onClick={() => setForm(null)}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={saving}
          disabled={!form.reference.trim()}
          onClick={() => { void saveReference(); }}
        >
          {form.id ? 'Save changes' : 'Add reference'}
        </Button>
      </Stack>
    </Stack>
  );

  const sectionHeading = (label: string, onAdd?: () => void) => (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5, minHeight: 28 }}>
      <Typography level="body-xs" fontWeight="lg" sx={{ color: 'neutral.600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      {onAdd && (
        <Button
          size="sm"
          variant="plain"
          startDecorator={<IconWrapper icon={FaPlus} />}
          onClick={onAdd}
          sx={{ minHeight: 0, py: 0.25, px: 0.75, fontSize: 'xs' }}
        >
          Add
        </Button>
      )}
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdTag}
          title="Supplier orders"
          description="One line per order: the supplier's own reference (not our lifecycle ref), what it is, when it's produced, where it's delivered and what's paid."
          action={addableSuppliers.length > 0 ? (
            <Dropdown>
              <MenuButton size="sm" variant="outlined" startDecorator={<IconWrapper icon={FaPlus} />}>
                Supplier
              </MenuButton>
              <Menu size="sm" placement="bottom-end" sx={{ maxHeight: 320, overflow: 'auto' }}>
                {addableSuppliers.map((row) => (
                  <MenuItem
                    key={row.id}
                    onClick={() => setExtraSupplierIds((current) => (
                      current.includes(row.id) ? current : [...current, row.id]
                    ))}
                  >
                    {row.supplier_name}
                  </MenuItem>
                ))}
              </Menu>
            </Dropdown>
          ) : undefined}
        />

        {error && <Alert color="danger" sx={{ mb: 1.25 }}>{error}</Alert>}
        {loading && <CircularProgress size="sm" />}

        {!loading && (
          <Stack gap={1.5}>
            {groups.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                Choose a supplier above, or use + Supplier.
              </Typography>
            )}

            {groups.map((group) => {
              const groupKey = group.supplierId || UNASSIGNED_KEY;
              const formHere = form?.groupKey === groupKey;
              return (
                <Sheet key={groupKey} variant="outlined" sx={{ p: 1.25, borderRadius: 'sm' }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>{group.supplierName}</Typography>

                  {sectionHeading('Orders', !readOnly && group.supplierId
                    ? () => addProductionDate(group.supplierId as string)
                    : undefined)}
                  <Stack gap={1.5}>
                    {group.productionDates.length === 0 && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No orders yet.</Typography>
                    )}
                    {group.productionDates.map((entry) => (
                      <Stack
                        key={entry.id}
                        direction={{ xs: 'column', md: 'row' }}
                        flexWrap="wrap"
                        gap={0.75}
                        alignItems={{ xs: 'stretch', md: 'flex-end' }}
                      >
                        <FormControl size="sm" sx={{ flex: '1 1 130px', minWidth: 120 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Their ref</FormLabel>
                          <CommitInput
                            value={entry.reference || ''}
                            disabled={readOnly}
                            placeholder="e.g. SO-48291"
                            onCommit={(value) => updateProductionDate(entry.id, 'reference', value.trim() || null)}
                          />
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 130 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Product</FormLabel>
                          <Select
                            size="sm"
                            value={entry.productType}
                            disabled={readOnly}
                            onChange={(_, value) => updateProductionDate(
                              entry.id,
                              'productType',
                              value || productionProductOptions(productTypes || undefined, entry.productType)[0] || 'Glass',
                            )}
                          >
                            {productionProductOptions(productTypes || undefined, entry.productType).map((type) => (
                              <Option key={type} value={type}>{type}</Option>
                            ))}
                          </Select>
                        </FormControl>
                        {!group.supplierId && (
                          <FormControl size="sm" sx={{ flex: 1, minWidth: 200 }}>
                            <FormLabel sx={ORDER_LABEL_SX}>Supplier</FormLabel>
                            <SupplierSelector
                              value={entry.supplierId}
                              onChange={(id: string) => updateProductionDate(entry.id, 'supplierId', id)}
                              supplierType={supplierCategoryForProduct(entry.productType)}
                            />
                          </FormControl>
                        )}
                        <FormControl size="sm" sx={{ minWidth: 140 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Production date</FormLabel>
                          <Input
                            type="date"
                            size="sm"
                            value={entry.date}
                            disabled={readOnly}
                            error={!entry.date}
                            onChange={(event) => updateProductionDate(entry.id, 'date', event.target.value)}
                          />
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 120 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Date is</FormLabel>
                          <Select
                            size="sm"
                            value={entry.dateStatus === 'provisional' ? 'provisional' : 'fixed'}
                            disabled={readOnly}
                            color={entry.dateStatus === 'provisional' ? 'warning' : 'neutral'}
                            onChange={(_, value) => updateProductionDate(entry.id, 'dateStatus', value === 'provisional' ? 'provisional' : 'fixed')}
                          >
                            <Option value="fixed">Fixed</Option>
                            <Option value="provisional">Provisional</Option>
                          </Select>
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 140 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Deliver to</FormLabel>
                          <Select
                            size="sm"
                            value={entry.deliveryTo ?? ''}
                            disabled={readOnly}
                            onChange={(_, value) => updateProductionDate(entry.id, 'deliveryTo', value === 'yard' || value === 'site' ? value : null)}
                          >
                            <Option value="">Not set</Option>
                            <Option value="yard">{DELIVERY_TO_LABELS.yard}</Option>
                            <Option value="site">{DELIVERY_TO_LABELS.site}</Option>
                          </Select>
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 115 }}>
                          <FormLabel sx={ORDER_LABEL_SX}>Paid</FormLabel>
                          <Select
                            size="sm"
                            value={entry.paid ?? ''}
                            disabled={readOnly}
                            onChange={(_, value) => updateProductionDate(entry.id, 'paid', value === '' ? null : (value || null))}
                          >
                            <Option value="">None</Option>
                            <Option value="Deposit">{PRODUCTION_PAID_LABELS.Deposit}</Option>
                            <Option value="Fully">{PRODUCTION_PAID_LABELS.Fully}</Option>
                          </Select>
                        </FormControl>
                        {!readOnly && (
                          <IconButton
                            size="sm"
                            color="danger"
                            variant="plain"
                            onClick={() => removeProductionDate(entry.id)}
                            aria-label="Delete order"
                          >
                            <IconWrapper icon={FaTrash} />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>

                  {(group.references.length > 0 || formHere) && (
                    <Box sx={{ mt: 1.5 }}>
                    {sectionHeading('Other references')}
                    <Stack gap={0.5}>
                      {group.references.map((row) => (
                        <Stack key={row.id} direction="row" alignItems="center" gap={0.5}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight="lg">{row.reference}</Typography>
                            {row.notes && (
                              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{row.notes}</Typography>
                            )}
                          </Box>
                          <Stack direction="row" gap={0}>
                            <IconButton
                              size="sm"
                              variant="plain"
                              disabled={saving || !!form}
                              onClick={() => startEditReference(groupKey, row)}
                              aria-label="Edit supplier reference"
                            >
                              <IconWrapper icon={FaEdit} />
                            </IconButton>
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="danger"
                              disabled={saving}
                              onClick={() => { void removeReference(row); }}
                              aria-label="Delete supplier reference"
                            >
                              <IconWrapper icon={FaTrash} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      ))}
                      {formHere && renderReferenceForm()}
                    </Stack>
                    </Box>
                  )}
                </Sheet>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplierOrdersCard;
